import type {
    RepeatableSubFieldDependencyBehaviour,
    RepeatableSubFieldDependencyContext,
    RepeatableSubFieldDependencyPatch,
    RepeatableSubFieldDependencyPatchFactory,
} from '../../interfaces/input.interface';

export interface ReactiveRepeatableCascadeDependencyConfig {
    readonly keepCurrentValueIfStillAvailable?: boolean;
}

export interface ReactiveRepeatableResolvedEntityPatchConfig<TEntity extends Record<string, unknown>> {
    readonly entityKey: string;
    readonly targetKey: string;
    readonly sourceKeys: readonly (keyof TEntity & string)[];
    readonly fallbackValue?: unknown;
}

/** Centralizes common row dependency behaviour for repeatable dropdowns. */
export class ReactiveRepeatableDropdownDependencyFactory {
    public static cascadeSingleOption(
        config: ReactiveRepeatableCascadeDependencyConfig = {},
    ): RepeatableSubFieldDependencyBehaviour {
        return {
            clearOnParentChange: true,
            autoSelect: 'singleOption',
            ...(config.keepCurrentValueIfStillAvailable !== undefined ? {
                keepCurrentValueIfStillAvailable: config.keepCurrentValueIfStillAvailable,
            } : {}),
        };
    }

    public static keepCurrentValue(): RepeatableSubFieldDependencyBehaviour {
        return { clearOnParentChange: false };
    }

    public static patchResolvedEntityValue<TEntity extends Record<string, unknown>>(
        config: ReactiveRepeatableResolvedEntityPatchConfig<TEntity>,
    ): RepeatableSubFieldDependencyPatchFactory {
        return (ctx: RepeatableSubFieldDependencyContext): RepeatableSubFieldDependencyPatch => {
            const fallbackValue = config.fallbackValue ?? '';
            const resolveEntity = ctx.resolveEntity;

            if (resolveEntity === undefined) {
                return { [config.targetKey]: fallbackValue };
            }

            const entity = resolveEntity<TEntity>(config.entityKey);

            return {
                [config.targetKey]: this.resolveFirstAvailableValue(entity, config.sourceKeys, fallbackValue),
            };
        };
    }

    private static resolveFirstAvailableValue<TEntity extends Record<string, unknown>>(
        entity: TEntity | null,
        sourceKeys: readonly (keyof TEntity & string)[],
        fallbackValue: unknown,
    ): unknown {
        if (entity === null) {
            return fallbackValue;
        }

        for (const key of sourceKeys) {
            const value = entity[key];

            if (value !== undefined && value !== null && String(value).trim() !== '') {
                return value;
            }
        }

        return fallbackValue;
    }
}
